/**
 * DecisionFlow — TenantProvision
 * ui/src/components/platform-admin/TenantProvision.jsx
 * OI-S228-04 / IAM-P1 / S233
 * S668 Step H — three-axis commercial form (spec_feature_gate_s665 §2.5, §3.7, §4.4, §5)
 *
 * Form for POST /api/v1/admin/tenants/provision (tool_admin only).
 *
 * is_demo=TRUE  → demo path (unchanged): credentials returned immediately.
 * is_demo=FALSE → production path: super_admin_email required, verification
 *                 email sent, shows PendingVerificationPanel on success.
 *
 * Three-axis commercial model:
 *   Axis 1 — Tier feature gates (summary line + feature comparison modal)
 *   Axis 2 — Plan packages (28 plan type checkboxes, trial, price)
 *   Axis 3 — Integration connectors (5 connector checkboxes, trial, price)
 *
 * Auth: session cookie (credentials: "include") — same as all other routes.
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Copy, CheckCircle2, Mail, X } from "lucide-react";

import Card from "../shared/Card.jsx";
import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";

// ---------------------------------------------------------------------------
// Axis 2 — all 28 plan type keys (spec_feature_gate_s665 §3.2)
// ---------------------------------------------------------------------------
const ALL_PLAN_KEYS = [
  { key: "feature.plan.transaction",     label: "Transaction-Level Crediting" },
  { key: "feature.plan.draw",            label: "Draw / Guarantee" },
  { key: "feature.plan.clawback",        label: "Clawback" },
  { key: "feature.plan.ramp",            label: "Ramp Schedules" },
  { key: "feature.plan.spiff",           label: "SPIFF / Tactical Incentives" },
  { key: "feature.plan.splits",          label: "Deal Splits" },
  { key: "feature.plan.overlay",         label: "Overlay Engine" },
  { key: "feature.plan.territory",       label: "Territory Compensation" },
  { key: "feature.plan.mbo",             label: "MBO Component" },
  { key: "feature.plan.nps",             label: "NPS Component" },
  { key: "feature.plan.adoption",        label: "Adoption Component" },
  { key: "feature.plan.kicker",          label: "Kicker Plans" },
  { key: "feature.plan.cosell",          label: "Co-Sell" },
  { key: "feature.plan.contest",         label: "Contests" },
  { key: "feature.plan.bonus_pool",      label: "Bonus Pools" },
  { key: "feature.plan.non_cash",        label: "Non-Cash Awards" },
  { key: "feature.plan.pod",             label: "Pod Compensation" },
  { key: "feature.plan.multi_year",      label: "Multi-Year Deals" },
  { key: "feature.plan.volume",          label: "Volume-Based Plans" },
  { key: "feature.plan.margin",          label: "Margin Compensation" },
  { key: "feature.plan.market_share",    label: "Market Share Plans" },
  { key: "feature.plan.ranking",         label: "Ranking Plans" },
  { key: "feature.plan.pharma",          label: "Pharma Script Plans" },
  { key: "feature.plan.channel",         label: "Channel Compensation" },
  { key: "feature.plan.true_up",         label: "True-Up Plans" },
  { key: "feature.plan.retention",       label: "Retention Plans" },
  { key: "feature.plan.plan_versioning", label: "Plan Versioning" },
  { key: "feature.plan.grr_nrr",         label: "GRR / NRR Plans" },
];

// ---------------------------------------------------------------------------
// Axis 3 — all 5 connector keys (spec_feature_gate_s665 §4.1)
// ---------------------------------------------------------------------------
const ALL_CONNECTOR_KEYS = [
  { key: "connector.crm.salesforce",  label: "Salesforce CRM" },
  { key: "connector.hris.workday",    label: "Workday HRIS" },
  { key: "connector.hris.mulesoft",   label: "MuleSoft Integration" },
  { key: "connector.crm.generic",     label: "Generic CRM (CSV/API)" },
  { key: "connector.hris.generic",    label: "Generic HRIS (CSV/API)" },
];

// Axis 1 — feature comparison matrix for modal (spec_feature_gate_s665 §2.1)
const TIER_MATRIX = [
  { label: "SSO / OIDC",             essentials: false, standard: true,  enterprise: true  },
  { label: "Excel Add-in",           essentials: false, standard: true,  enterprise: true  },
  { label: "FX Rate Management",     essentials: false, standard: false, enterprise: true  },
  { label: "Multi-currency",         essentials: false, standard: false, enterprise: true  },
  { label: "HRIS Integration",       essentials: false, standard: false, enterprise: true  },
  { label: "CRM + Quota Sync",       essentials: false, standard: false, enterprise: true  },
  { label: "AOP Governance",         essentials: false, standard: false, enterprise: true  },
  { label: "Scenario Planning",      essentials: false, standard: false, enterprise: true  },
  { label: "Agent Trust Center",     essentials: false, standard: false, enterprise: true  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 63);
}

function FieldRow({ label, required, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-tok-sm font-medium text-[rgb(var(--color-text))]">
        {label}
        {required && <span className="ml-0.5 text-[rgb(var(--color-danger))]">*</span>}
      </label>
      {children}
      {hint && (
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">{hint}</p>
      )}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, disabled, mono, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50 ${mono ? "font-mono" : ""}`}
    />
  );
}

function SelectInput({ value, onChange, disabled, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
    >
      {children}
    </select>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
      {children}
    </div>
  );
}

function BillingTriadRow({ trialLabel, trialValue, onTrialChange, priceLabel, priceValue, onPriceChange, disabled }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <FieldRow label={trialLabel} hint="0 = billing starts immediately">
        <div className="flex items-center gap-2">
          <TextInput
            value={trialValue}
            onChange={onTrialChange}
            placeholder="0"
            disabled={disabled}
          />
          <span className="shrink-0 text-tok-sm text-[rgb(var(--color-text-muted))]">months free</span>
        </div>
      </FieldRow>
      <FieldRow label={priceLabel} hint="USD / month · $0 = no charge">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-tok-sm text-[rgb(var(--color-text-muted))]">$</span>
          <TextInput
            value={priceValue}
            onChange={onPriceChange}
            placeholder="0"
            disabled={disabled}
          />
        </div>
      </FieldRow>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Axis 1 — Feature Comparison Modal
// ---------------------------------------------------------------------------

function FeatureComparisonModal({ tier, onClose }) {
  const CHECK = <span className="font-bold text-[rgb(var(--color-success))]">✓</span>;
  const CROSS = <span className="text-[rgb(var(--color-text-muted))] opacity-40">✗</span>;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-[rgb(var(--color-surface))] shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4">
          <div>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Platform Feature Comparison</p>
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">9 platform capability gates · all other features ungated</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto px-5 py-4">
          <table className="w-full text-tok-xs">
            <thead>
              <tr className="border-b border-[rgb(var(--color-border))]">
                <th className="pb-2 text-left font-semibold text-[rgb(var(--color-text-muted))]">Feature</th>
                <th className={`pb-2 text-center font-semibold ${tier === "essentials" ? "text-[rgb(var(--color-primary))]" : "text-[rgb(var(--color-text-muted))]"}`}>Essentials</th>
                <th className={`pb-2 text-center font-semibold ${tier === "standard"   ? "text-[rgb(var(--color-primary))]" : "text-[rgb(var(--color-text-muted))]"}`}>Standard Plus</th>
                <th className={`pb-2 text-center font-semibold ${tier === "enterprise" ? "text-[rgb(var(--color-primary))]" : "text-[rgb(var(--color-text-muted))]"}`}>Enterprise</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgb(var(--color-border))]">
              {TIER_MATRIX.map(({ label, essentials, standard, enterprise }) => (
                <tr key={label} className={
                  (tier === "essentials" && essentials) ||
                  (tier === "standard"   && standard)   ||
                  (tier === "enterprise" && enterprise)
                    ? "bg-[rgb(var(--color-surface-muted))]"
                    : ""
                }>
                  <td className="py-1.5 pr-4 text-[rgb(var(--color-text))]">{label}</td>
                  <td className="py-1.5 text-center">{essentials ? CHECK : CROSS}</td>
                  <td className="py-1.5 text-center">{standard   ? CHECK : CROSS}</td>
                  <td className="py-1.5 text-center">{enterprise ? CHECK : CROSS}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-tok-xs text-[rgb(var(--color-text-muted))]">
            AI Copilot, Disputes Workflow, PDF Statements, Finance Shell, and full audit trail are ungated on all tiers.
          </p>
        </div>

        <div className="border-t border-[rgb(var(--color-border))] px-5 py-3 text-right">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Demo path — one-time credentials panel (unchanged behaviour)
// ---------------------------------------------------------------------------

function CredentialsPanel({ email, password, onDone }) {
  const { t } = useTranslation("platform");
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(null);

  async function copyToClipboard(text, field) {
    await navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <Card title={t("provision.credentials.title")}>
      <div className="mb-4 rounded-xl border border-[rgb(var(--color-warning))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
        <p className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">
          {t("provision.credentials.warningTitle")}
        </p>
        <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
          {t("provision.credentials.warningBody")}
        </p>
      </div>

      <div className="space-y-3">
        {/* Email */}
        <div className="flex items-center justify-between gap-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
          <div>
            <div className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
              {t("provision.credentials.emailLabel")}
            </div>
            <div className="mt-0.5 font-mono text-tok-sm text-[rgb(var(--color-text))]">{email}</div>
          </div>
          <button
            onClick={() => copyToClipboard(email, "email")}
            className="shrink-0 rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface))] transition"
          >
            {copied === "email"
              ? <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))]" />
              : <Copy className="h-4 w-4" />}
          </button>
        </div>

        {/* Password */}
        <div className="flex items-center justify-between gap-3 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-tok-xs font-semibold text-[rgb(var(--color-text-muted))]">
              {t("provision.credentials.passwordLabel")}
            </div>
            <div className="mt-0.5 font-mono text-tok-sm text-[rgb(var(--color-text))] break-all">
              {showPassword ? password : "••••••••••••••••"}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              onClick={() => setShowPassword((v) => !v)}
              className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface))] transition"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              onClick={() => copyToClipboard(password, "password")}
              className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface))] transition"
            >
              {copied === "password"
                ? <CheckCircle2 className="h-4 w-4 text-[rgb(var(--color-success))]" />
                : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <Button onClick={onDone}>{t("provision.credentials.done")}</Button>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Production path — pending verification panel
// ---------------------------------------------------------------------------

function PendingVerificationPanel({ email, tenantName, onDone }) {
  return (
    <Card title="Verification email sent">
      {/* Status icon */}
      <div className="mb-5 flex items-center gap-4 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] bg-opacity-10">
          <Mail className="h-5 w-5 text-[rgb(var(--color-primary))]" />
        </div>
        <div>
          <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
            Check inbox at <span className="font-mono">{email}</span>
          </p>
          <p className="mt-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            A verification link has been sent. The link expires in 72 hours.
          </p>
        </div>
      </div>

      {/* What happens next */}
      <div className="mb-5 space-y-2">
        <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          What happens next
        </p>
        <ol className="space-y-2 text-tok-sm text-[rgb(var(--color-text-muted))]">
          <li className="flex gap-2">
            <span className="shrink-0 font-semibold text-[rgb(var(--color-text))]">1.</span>
            The super admin at <strong className="text-[rgb(var(--color-text))]">{tenantName}</strong> receives the email.
          </li>
          <li className="flex gap-2">
            <span className="shrink-0 font-semibold text-[rgb(var(--color-text))]">2.</span>
            They click the link, set their password, and the tenant activates automatically.
          </li>
          <li className="flex gap-2">
            <span className="shrink-0 font-semibold text-[rgb(var(--color-text))]">3.</span>
            The tenant status changes from <span className="font-mono text-[rgb(var(--color-text))]">pending_verification</span> to <span className="font-mono text-[rgb(var(--color-text))]">trial</span>.
          </li>
        </ol>
      </div>

      {/* Note on email failure */}
      <div className="mb-5 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
          If the email does not arrive within a few minutes, check the spam folder.
          The tenant remains in <span className="font-mono">pending_verification</span> status
          until the link is clicked.
        </p>
      </div>

      <Button onClick={onDone}>Provision another tenant</Button>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// TenantProvision form
// ---------------------------------------------------------------------------

// TIER_SEAT_DEFAULTS removed S660 — DB is single source of truth.
// Seat floor auto-populated from GET /admin/tier-config on tier change.

const INITIAL_FORM = {
  tenant_name:             "",
  tenant_slug:             "",
  subscription_tier:       "essentials",
  hq_country_code:         "US",
  reporting_currency_code: "USD",
  fiscal_year_start_month: "1",
  fiscal_year_label:       "",
  period_code:             "",
  period_type:             "monthly",
  period_start:            "",
  period_end:              "",
  max_positions:           "",
  // Commercial fields (production tenants only)
  legal_entity_name:       "",
  billing_email:           "",
  ein:                     "",
  po_number:               "",
  msa_reference:           "",
  dpa_reference:           "",
};

const INITIAL_BILLING = {
  contract_months:        "12",
  // Axis 1
  seat_trial_months:      "0",
  seat_price_override:    "",   // empty = use list price from ref_subscription_tier
  // Axis 2
  package_name:           "default_plan_bundle",
  package_trial_months:   "0",
  package_price_usd:      "0",
  // Axis 3
  connector_trial_months: "0",
  connector_price_usd:    "0",
};

const INITIAL_PLAN_KEYS  = new Set(ALL_PLAN_KEYS.map(p => p.key));
const INITIAL_CONN_KEYS  = new Set(ALL_CONNECTOR_KEYS.map(c => c.key));

export default function TenantProvision() {
  const { t } = useTranslation("platform");

  const [form,             setForm]             = useState(INITIAL_FORM);
  const [slugEdited,       setSlugEdited]       = useState(false);
  const [submitting,       setSubmitting]       = useState(false);
  const [error,            setError]            = useState(null);
  const [credentials,      setCredentials]      = useState(null);
  const [pendingVerify,    setPendingVerify]    = useState(null);
  const [isDemo,           setIsDemo]           = useState(true);
  const [superAdminEmail,  setSuperAdminEmail]  = useState("");
  const [mustChangePw,     setMustChangePw]     = useState(false);

  // Axis 1 — tier features (D1 — S566 — OI-PLATFORM-FEATURE-ENABLEMENT)
  const [tierFeatures,     setTierFeatures]     = useState(null);
  const [tierModal,        setTierModal]        = useState(false);

  // Axis 2 — plan types (S668 Step H)
  const [planKeys,         setPlanKeys]         = useState(INITIAL_PLAN_KEYS);

  // Axis 3 — connectors (S668 Step H)
  const [connKeys,         setConnKeys]         = useState(INITIAL_CONN_KEYS);

  // Billing schedule fields (S668 Step H)
  const [billing,          setBilling]          = useState(INITIAL_BILLING);

  // S660: tier band config from DB — replaces TIER_SEAT_DEFAULTS hardcoded dict
  const [tierConfig,       setTierConfig]       = useState(null);

  // Fetch tier-config on mount so band hints are available immediately
  useEffect(() => {
    fetch(`${API_BASE}/admin/tier-config`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => setTierConfig(data?.tiers ?? data ?? null))
      .catch(() => setTierConfig(null));
  }, []);

  useEffect(() => {
    if (!slugEdited) {
      setForm((prev) => ({ ...prev, tenant_slug: slugify(prev.tenant_name) }));
    }
  }, [form.tenant_name, slugEdited]);

  // D1 — fetch feature flags whenever tier selection changes
  useEffect(() => {
    let cancelled = false;
    setTierFeatures(null);
    fetch(`${API_BASE}/admin/tier-features?tier=${form.subscription_tier}`, {
      credentials: "include",
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data) => { if (!cancelled) setTierFeatures(data); })
      .catch(() => { if (!cancelled) setTierFeatures("error"); });
    return () => { cancelled = true; };
  }, [form.subscription_tier]);

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function handleSlugChange(e) {
    setSlugEdited(true);
    setForm((prev) => ({ ...prev, tenant_slug: e.target.value }));
  }

  function handleBillingChange(field) {
    return (e) => setBilling((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function togglePlanKey(key) {
    setPlanKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function toggleConnKey(key) {
    setConnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!isDemo && !superAdminEmail.trim()) {
      setError("Super Admin email is required for production tenants.");
      setSubmitting(false);
      return;
    }

    function parseSeat(val) {
      const n = parseInt(val, 10);
      return isNaN(n) ? null : n;
    }
    function parseInt0(val) {
      const n = parseInt(val, 10);
      return isNaN(n) ? 0 : n;
    }
    function parseFloat0(val) {
      const n = parseFloat(val);
      return isNaN(n) ? 0.0 : n;
    }

    const body = {
      tenant_name:             form.tenant_name.trim(),
      tenant_slug:             form.tenant_slug.trim(),
      subscription_tier:       form.subscription_tier,
      hq_country_code:         form.hq_country_code.trim().toUpperCase(),
      reporting_currency_code: form.reporting_currency_code.trim().toUpperCase(),
      fiscal_year_start_month: parseInt(form.fiscal_year_start_month, 10),
      fiscal_year_label:       form.fiscal_year_label.trim(),
      period_code:             form.period_code.trim(),
      period_type:             form.period_type,
      period_start:            form.period_start,
      period_end:              form.period_end,
      is_demo:                 isDemo,
      max_positions:           parseSeat(form.max_positions),
      ...(!isDemo && { super_admin_email: superAdminEmail.trim() }),
      ...(!isDemo && { must_change_password: mustChangePw }),
      // Commercial fields — only sent for production tenants
      ...(!isDemo && form.legal_entity_name.trim() && { legal_entity_name: form.legal_entity_name.trim() }),
      ...(!isDemo && form.billing_email.trim()      && { billing_email:      form.billing_email.trim() }),
      ...(!isDemo && form.ein.trim()                && { ein:                form.ein.trim() }),
      ...(!isDemo && form.po_number.trim()          && { po_number:          form.po_number.trim() }),
      ...(!isDemo && form.msa_reference.trim()      && { msa_reference:      form.msa_reference.trim() }),
      ...(!isDemo && form.dpa_reference.trim()      && { dpa_reference:      form.dpa_reference.trim() }),
      // Three-axis billing schedule (S668 Step H — spec_feature_gate_s665 §5)
      contract_months:        parseInt0(billing.contract_months) || 12,
      // Axis 1
      seat_trial_months:      parseInt0(billing.seat_trial_months),
      seat_price_override:    billing.seat_price_override.trim() !== ""
                                ? parseFloat0(billing.seat_price_override)
                                : null,
      // Axis 2
      package_name:           billing.package_name.trim() || "default_plan_bundle",
      package_plan_keys:      [...planKeys],
      package_trial_months:   parseInt0(billing.package_trial_months),
      package_price_usd:      parseFloat0(billing.package_price_usd),
      // Axis 3
      connector_keys:         [...connKeys],
      connector_trial_months: parseInt0(billing.connector_trial_months),
      connector_price_usd:    parseFloat0(billing.connector_price_usd),
    };

    try {
      const res = await fetch(`${API_BASE}/admin/tenants/provision`, {
        method:      "POST",
        headers:     { "Content-Type": "application/json" },
        credentials: "include",
        body:        JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (res.status === 409) {
          throw new Error(t("provision.error.slugConflict"));
        }
        throw new Error(data?.detail?.error?.message ?? `Error ${res.status}`);
      }

      if (isDemo) {
        setCredentials({
          email:    data.super_admin_email,
          password: data.super_admin_password,
        });
      } else {
        setPendingVerify({
          email:      data.super_admin_email,
          tenantName: form.tenant_name.trim(),
        });
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleDone() {
    setCredentials(null);
    setPendingVerify(null);
    setForm(INITIAL_FORM);
    setSlugEdited(false);
    setIsDemo(true);
    setSuperAdminEmail("");
    setMustChangePw(false);
    setPlanKeys(INITIAL_PLAN_KEYS);
    setConnKeys(INITIAL_CONN_KEYS);
    setBilling(INITIAL_BILLING);
    setError(null);
  }

  // --- Success states ---

  if (credentials) {
    return (
      <CredentialsPanel
        email={credentials.email}
        password={credentials.password}
        onDone={handleDone}
      />
    );
  }

  if (pendingVerify) {
    return (
      <PendingVerificationPanel
        email={pendingVerify.email}
        tenantName={pendingVerify.tenantName}
        onDone={handleDone}
      />
    );
  }

  // Derived counts for Axis 1 summary
  const axis1EnabledCount = tierFeatures && tierFeatures !== "error"
    ? tierFeatures.enabled_count
    : null;

  // --- Form ---

  return (
    <>
      {tierModal && (
        <FeatureComparisonModal
          tier={form.subscription_tier}
          onClose={() => setTierModal(false)}
        />
      )}

      <Card title={t("provision.title")}>
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Tenant Identity */}
          <div className="space-y-3">
            <SectionLabel>{t("provision.section.identity")}</SectionLabel>

            <div className="grid gap-3 sm:grid-cols-2">
              <FieldRow label={t("provision.field.tenantName")} required>
                <TextInput
                  value={form.tenant_name}
                  onChange={handleChange("tenant_name")}
                  placeholder={t("provision.field.tenantNamePlaceholder")}
                  disabled={submitting}
                />
              </FieldRow>

              <FieldRow
                label={t("provision.field.tenantSlug")}
                required
                hint={t("provision.field.tenantSlugHint")}
              >
                <TextInput
                  value={form.tenant_slug}
                  onChange={handleSlugChange}
                  placeholder="acme-corp"
                  disabled={submitting}
                  mono
                />
              </FieldRow>

              <FieldRow label={t("provision.field.subscriptionTier")} required>
                <SelectInput
                  value={form.subscription_tier}
                  onChange={(e) => {
                    const tier = e.target.value;
                    const floor = tierConfig?.[tier]?.min_positions ?? "";
                    setForm((prev) => ({
                      ...prev,
                      subscription_tier: tier,
                      max_positions: floor !== "" ? String(floor) : "",
                    }));
                  }}
                  disabled={submitting}
                >
                  <option value="essentials">essentials</option>
                  <option value="standard">standard</option>
                  <option value="enterprise">enterprise</option>
                </SelectInput>
              </FieldRow>

              <FieldRow label={t("provision.field.hqCountryCode")} required>
                <TextInput
                  value={form.hq_country_code}
                  onChange={handleChange("hq_country_code")}
                  placeholder="US"
                  disabled={submitting}
                />
              </FieldRow>

              <FieldRow label={t("provision.field.reportingCurrency")} required>
                <TextInput
                  value={form.reporting_currency_code}
                  onChange={handleChange("reporting_currency_code")}
                  placeholder="USD"
                  disabled={submitting}
                />
              </FieldRow>
            </div>
          </div>

          {/* Fiscal Configuration */}
          <div className="space-y-3">
            <SectionLabel>{t("provision.section.fiscal")}</SectionLabel>

            <div className="grid gap-3 sm:grid-cols-2">
              <FieldRow label={t("provision.field.fiscalYearStartMonth")} required>
                <SelectInput
                  value={form.fiscal_year_start_month}
                  onChange={handleChange("fiscal_year_start_month")}
                  disabled={submitting}
                >
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                  ))}
                </SelectInput>
              </FieldRow>

              <FieldRow label={t("provision.field.fiscalYearLabel")} required>
                <TextInput
                  value={form.fiscal_year_label}
                  onChange={handleChange("fiscal_year_label")}
                  placeholder="FY2026"
                  disabled={submitting}
                />
              </FieldRow>

              <FieldRow label={t("provision.field.periodCode")} required>
                <TextInput
                  value={form.period_code}
                  onChange={handleChange("period_code")}
                  placeholder="FY2026-M01"
                  disabled={submitting}
                  mono
                />
              </FieldRow>

              <FieldRow label={t("provision.field.periodType")} required>
                <SelectInput
                  value={form.period_type}
                  onChange={handleChange("period_type")}
                  disabled={submitting}
                >
                  <option value="monthly">monthly</option>
                  <option value="quarterly">quarterly</option>
                  <option value="annual">annual</option>
                </SelectInput>
              </FieldRow>

              <FieldRow label={t("provision.field.periodStart")} required>
                <input
                  type="date"
                  value={form.period_start}
                  onChange={handleChange("period_start")}
                  disabled={submitting}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                />
              </FieldRow>

              <FieldRow label={t("provision.field.periodEnd")} required>
                <input
                  type="date"
                  value={form.period_end}
                  onChange={handleChange("period_end")}
                  disabled={submitting}
                  className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))] disabled:opacity-50"
                />
              </FieldRow>
            </div>
          </div>

          {/* Seat Configuration — S660: API-driven band hints, reps only */}
          <div className="space-y-3">
            <SectionLabel>Seat Configuration</SectionLabel>
            {tierConfig && tierConfig[form.subscription_tier] && (
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {form.subscription_tier === "enterprise"
                  ? `Enterprise: ${tierConfig.enterprise.min_positions}+ seats · $${tierConfig.enterprise.price_per_seat_usd}/rep/month · No ceiling`
                  : `${form.subscription_tier.charAt(0).toUpperCase() + form.subscription_tier.slice(1)}: ${tierConfig[form.subscription_tier].min_positions}–${tierConfig[form.subscription_tier].max_positions} seats · $${tierConfig[form.subscription_tier].price_per_seat_usd}/rep/month`
                }
              </p>
            )}
            <FieldRow
              label="Max Positions (Reps)"
              hint={
                tierConfig?.[form.subscription_tier]
                  ? form.subscription_tier === "enterprise"
                    ? `Floor: ${tierConfig.enterprise.min_positions}. Blank = tier floor applied by server.`
                    : `Band: ${tierConfig[form.subscription_tier].min_positions}–${tierConfig[form.subscription_tier].max_positions}. Blank = tier floor applied by server.`
                  : "Leave blank for server to apply tier floor."
              }
            >
              <TextInput
                value={form.max_positions}
                onChange={handleChange("max_positions")}
                placeholder={
                  tierConfig?.[form.subscription_tier]
                    ? String(tierConfig[form.subscription_tier].min_positions)
                    : form.subscription_tier === "enterprise" ? "100" : ""
                }
                disabled={submitting}
              />
            </FieldRow>
          </div>

          {/* ── Axis 1 — Tier Features ─────────────────────────────────────── */}
          <div className="space-y-3">
            <SectionLabel>Axis 1 — Platform Features</SectionLabel>

            {/* Summary line + modal trigger */}
            <div className="flex items-center justify-between rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3">
              <div>
                {tierFeatures === null && (
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading feature catalog…</p>
                )}
                {tierFeatures === "error" && (
                  <p className="text-tok-sm text-[rgb(var(--color-danger))]">Could not load feature catalog.</p>
                )}
                {tierFeatures && tierFeatures !== "error" && (
                  <p className="text-tok-sm text-[rgb(var(--color-text))]">
                    <span className="font-semibold">{tierFeatures.enabled_count}</span>
                    {" "}platform {tierFeatures.enabled_count === 1 ? "feature" : "features"} enabled for{" "}
                    <span className="font-semibold">{form.subscription_tier}</span>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setTierModal(true)}
                className="shrink-0 text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline"
              >
                View feature comparison
              </button>
            </div>

            {/* Axis 1 billing */}
            <BillingTriadRow
              trialLabel="Tier free trial"
              trialValue={billing.seat_trial_months}
              onTrialChange={handleBillingChange("seat_trial_months")}
              priceLabel="Per-seat price override"
              priceValue={billing.seat_price_override}
              onPriceChange={handleBillingChange("seat_price_override")}
              disabled={submitting}
            />
            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              Leave price blank to use list price from tier config (${tierConfig?.[form.subscription_tier]?.price_per_seat_usd ?? "—"}/rep/month).
            </p>
          </div>

          {/* ── Axis 2 — Plan Packages ────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>Axis 2 — Plan Packages</SectionLabel>
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {planKeys.size} / {ALL_PLAN_KEYS.length} selected
              </span>
            </div>

            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              All 28 plan types are enabled by default. Deselect to restrict this tenant's plan library.
            </p>

            {/* Select all / Deselect all */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPlanKeys(new Set(ALL_PLAN_KEYS.map(p => p.key)))}
                className="text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline"
                disabled={submitting}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setPlanKeys(new Set())}
                className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:underline"
                disabled={submitting}
              >
                Deselect all
              </button>
            </div>

            {/* 28 checkboxes in 2-column grid */}
            <div className="grid gap-1.5 sm:grid-cols-2 rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3">
              {ALL_PLAN_KEYS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={planKeys.has(key)}
                    onChange={() => togglePlanKey(key)}
                    disabled={submitting}
                    className="h-3.5 w-3.5 rounded border-[rgb(var(--color-border))]"
                  />
                  <span className="text-tok-xs text-[rgb(var(--color-text))]">{label}</span>
                </label>
              ))}
            </div>

            {/* Package name (only relevant if not all selected) */}
            {planKeys.size < ALL_PLAN_KEYS.length && (
              <FieldRow
                label="Package name"
                hint="Name for this custom plan bundle — used in billing schedule and tenant contract."
              >
                <TextInput
                  value={billing.package_name}
                  onChange={handleBillingChange("package_name")}
                  placeholder="custom_plan_bundle"
                  disabled={submitting}
                  mono
                />
              </FieldRow>
            )}

            {/* Axis 2 billing */}
            <BillingTriadRow
              trialLabel="Plan package free trial"
              trialValue={billing.package_trial_months}
              onTrialChange={handleBillingChange("package_trial_months")}
              priceLabel="Package price ($/month)"
              priceValue={billing.package_price_usd}
              onPriceChange={handleBillingChange("package_price_usd")}
              disabled={submitting}
            />
          </div>

          {/* ── Axis 3 — Integration Connectors ──────────────────────────── */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <SectionLabel>Axis 3 — Integration Connectors</SectionLabel>
              <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                {connKeys.size} / {ALL_CONNECTOR_KEYS.length} selected
              </span>
            </div>

            <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
              All connectors enabled by default. Deselect to restrict integrations for this tenant.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConnKeys(new Set(ALL_CONNECTOR_KEYS.map(c => c.key)))}
                className="text-tok-xs font-medium text-[rgb(var(--color-primary))] hover:underline"
                disabled={submitting}
              >
                Select all
              </button>
              <button
                type="button"
                onClick={() => setConnKeys(new Set())}
                className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))] hover:underline"
                disabled={submitting}
              >
                Deselect all
              </button>
            </div>

            <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-3 space-y-1.5">
              {ALL_CONNECTOR_KEYS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={connKeys.has(key)}
                    onChange={() => toggleConnKey(key)}
                    disabled={submitting}
                    className="h-3.5 w-3.5 rounded border-[rgb(var(--color-border))]"
                  />
                  <span className="text-tok-xs text-[rgb(var(--color-text))]">{label}</span>
                  <span className="text-tok-xs font-mono text-[rgb(var(--color-text-muted))]">({key})</span>
                </label>
              ))}
            </div>

            {/* Axis 3 billing */}
            <BillingTriadRow
              trialLabel="Connector free trial"
              trialValue={billing.connector_trial_months}
              onTrialChange={handleBillingChange("connector_trial_months")}
              priceLabel="Connector price ($/month)"
              priceValue={billing.connector_price_usd}
              onPriceChange={handleBillingChange("connector_price_usd")}
              disabled={submitting}
            />
          </div>

          {/* ── Billing Schedule Contract Length ─────────────────────────── */}
          <div className="space-y-3">
            <SectionLabel>Billing Schedule</SectionLabel>
            <FieldRow
              label="Contract length (months)"
              hint="Number of forward billing rows generated at provision. Default 12 = 1 year."
            >
              <TextInput
                value={billing.contract_months}
                onChange={handleBillingChange("contract_months")}
                placeholder="12"
                disabled={submitting}
              />
            </FieldRow>
          </div>

          {/* Commercial section — S660: legal entity + compliance fields (production tenants only) */}
          {!isDemo && (
            <div className="space-y-3">
              <SectionLabel>Commercial Details</SectionLabel>
              <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
                Required before first invoice. Stored on tenant record for contract and billing use.
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <FieldRow label="Legal Entity Name" hint="Full registered legal name (may differ from trading name)">
                  <TextInput
                    value={form.legal_entity_name ?? ""}
                    onChange={handleChange("legal_entity_name")}
                    placeholder="Acme Corporation Inc."
                    disabled={submitting}
                  />
                </FieldRow>
                <FieldRow label="Billing / AP Email" hint="Where invoices are sent">
                  <TextInput
                    value={form.billing_email ?? ""}
                    onChange={handleChange("billing_email")}
                    placeholder="ap@customer.com"
                    type="email"
                    disabled={submitting}
                  />
                </FieldRow>
                <FieldRow label="EIN (US)" hint="XX-XXXXXXX · Required for US invoicing · Encrypted at rest">
                  <TextInput
                    value={form.ein ?? ""}
                    onChange={handleChange("ein")}
                    placeholder="12-3456789"
                    disabled={submitting}
                  />
                </FieldRow>
                <FieldRow label="PO Number" hint="Current purchase order number (optional)">
                  <TextInput
                    value={form.po_number ?? ""}
                    onChange={handleChange("po_number")}
                    placeholder="PO-2026-001"
                    disabled={submitting}
                  />
                </FieldRow>
                <FieldRow label="MSA Reference" hint="Document reference or URL to signed MSA">
                  <TextInput
                    value={form.msa_reference ?? ""}
                    onChange={handleChange("msa_reference")}
                    placeholder="MSA-2026-001 or DocuSign URL"
                    disabled={submitting}
                  />
                </FieldRow>
                <FieldRow label="DPA Reference" hint="Document reference or URL to signed DPA (required for EU/CA data)">
                  <TextInput
                    value={form.dpa_reference ?? ""}
                    onChange={handleChange("dpa_reference")}
                    placeholder="DPA-2026-001 or DocuSign URL"
                    disabled={submitting}
                  />
                </FieldRow>
              </div>
            </div>
          )}

          {/* is_demo toggle */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_demo"
                checked={isDemo}
                onChange={(e) => { setIsDemo(e.target.checked); setSuperAdminEmail(""); }}
                disabled={submitting}
                className="h-4 w-4 rounded border-[rgb(var(--color-border))]"
              />
              <label htmlFor="is_demo" className="text-tok-sm text-[rgb(var(--color-text))]">
                {t("provision.field.isDemo")}
              </label>
            </div>

            {!isDemo && (
              <FieldRow
                label="Super Admin Email"
                required
                hint="A verification email will be sent. The tenant activates when the super admin sets their password."
              >
                <TextInput
                  value={superAdminEmail}
                  onChange={(e) => setSuperAdminEmail(e.target.value)}
                  placeholder="admin@customer.com"
                  type="email"
                  disabled={submitting}
                />
              </FieldRow>
            )}

            {/* spec_auth_identity_s661.md §12.8 — must_change_password checkbox */}
            {!isDemo && (
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="must_change_password"
                  checked={mustChangePw}
                  onChange={(e) => setMustChangePw(e.target.checked)}
                  disabled={submitting}
                  className="mt-0.5 h-4 w-4 rounded border-[rgb(var(--color-border))]"
                />
                <div>
                  <label
                    htmlFor="must_change_password"
                    className="text-tok-sm text-[rgb(var(--color-text))] cursor-pointer"
                  >
                    Require password change on first login
                  </label>
                  <p className="text-tok-xs text-[rgb(var(--color-text-muted))] mt-0.5">
                    Check this if the tenant wants to set their own password immediately after first sign-in.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] px-3 py-2 text-tok-sm text-[rgb(var(--color-danger))]">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? t("provision.submitting") : t("provision.submit")}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={submitting}
              onClick={handleDone}
            >
              {t("provision.reset")}
            </Button>
          </div>

        </form>
      </Card>
    </>
  );
}
